import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import SportPanelSection from 'applications/sports/components/search/search-discovery-view/sport-panel-section/index.js';

const { FlexPane, Image, Text } = MojitoPresentation.Components;
const { useMenuContent } = MojitoModules.Common.Hooks;
const { selectTimeZoneGmt } = MojitoServices.UserSettings.selectors;
const {
    selectors: { selectFavoriteSports },
} = MojitoModules.SportsMenu;
const { byDisplayOrder } = MojitoPresentation.Utils.SortingHelper;

const sortWithFavoritesFirst = (sports, favoriteSports) => {
    const favoriteSet = new Set(favoriteSports);
    return [...sports].sort((a, b) => {
        const aFav = favoriteSet.has(a.id);
        const bFav = favoriteSet.has(b.id);
        if (aFav !== bFav) {
            return aFav ? -1 : 1;
        }
        return byDisplayOrder(a, b);
    });
};

const SportsListPanel = props => {
    const {
        onNavigate,
        sports,
        mojitoTools: { config, stringResolver },
    } = props;
    const [expandedSportId, setExpandedSportId] = useState();
    const timeZone = useSelector(selectTimeZoneGmt);
    const favoriteSports = useSelector(selectFavoriteSports);
    // Only the expanded sport needs its competitions group, the menu subscription itself is shared.
    const contentInfo = useMenuContent(expandedSportId, timeZone);

    const sortedSports = useMemo(
        () => sortWithFavoritesFirst(sports, favoriteSports),
        [sports, favoriteSports]
    );

    const hasExpandedInitialSport = useRef(false);
    useEffect(() => {
        if (!hasExpandedInitialSport.current && sortedSports.length) {
            hasExpandedInitialSport.current = true;
            setExpandedSportId(sortedSports[0].id);
        }
    }, [sortedSports]);

    return (
        <FlexPane config={config.container} class="ta-sportsListPanel">
            <FlexPane config={config.titleContainer}>
                <Image config={config.titleIcon} />
                <Text config={config.title}>
                    {stringResolver.resolveString('$SEARCH_DISCOVERY_VIEW.SPORTS_SECTION_TITLE')}
                </Text>
            </FlexPane>
            <FlexPane config={config.sportsContainer}>
                {sortedSports.map(sport => {
                    const expanded = expandedSportId === sport.id;
                    return (
                        <SportPanelSection
                            key={sport.id}
                            config={config.sportPanelSection}
                            sport={sport}
                            isFavoriteSport={favoriteSports?.includes(sport.id)}
                            expanded={expanded}
                            competitionsGroupId={
                                expanded ? contentInfo?.competitions?.group : undefined
                            }
                            onToggle={() =>
                                setExpandedSportId(current =>
                                    current === sport.id ? undefined : sport.id
                                )
                            }
                            onNavigate={onNavigate}
                        />
                    );
                })}
            </FlexPane>
        </FlexPane>
    );
};

export default SportsListPanel;
